import React, { useState, useCallback } from 'react';
import { Card, Form, Button, Alert, ProgressBar, Row, Col, Table, Badge } from 'react-bootstrap';
import { FileEarmarkExcel, Download, CheckCircle, XCircle, InfoCircle } from 'react-bootstrap-icons';
import studentService from '../../services/studentService';

const ExcelUpload = ({ onExcelUploaded }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    
    if (!file) {
      setSelectedFile(null);
      setUploadResult(null);
      return;
    }

    // File validation
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB limit

    if (!validTypes.includes(file.type)) {
      alert('Please select a valid Excel file (.xlsx or .xls)');
      event.target.value = '';
      return;
    }

    if (file.size > maxSize) {
      alert('File size too large. Maximum size allowed is 10MB.');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    setUploadResult(null);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const result = await studentService.uploadExcel(selectedFile);
      setUploadResult(result.summary);
      
      if (onExcelUploaded) {
        onExcelUploaded(result.summary);
      }
      
      // Reset file selection
      setSelectedFile(null);
      document.getElementById('excel-upload').value = '';
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      alert('Failed to upload Excel file: ' + errorMessage);
    } finally {
      setUploading(false);
    }
  }, [selectedFile, onExcelUploaded]);

  const downloadSample = useCallback(async () => {
    setDownloadingTemplate(true);
    try {
      const blob = await studentService.downloadSampleExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student-import-template-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download sample template. Please try again.');
    } finally {
      setDownloadingTemplate(false);
    }
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getUploadResultVariant = () => {
    if (!uploadResult) return 'info';
    if (uploadResult.failed === 0 && uploadResult.duplicates === 0) return 'success';
    if (uploadResult.successful > 0) return 'warning';
    return 'danger';
  };

  return (
    <Card className="shadow-sm rounded border-0">
      <Card.Header className="d-flex align-items-center bg-primary text-white">
        <FileEarmarkExcel size={24} className="me-2" />
        <h5 className="mb-0">Bulk Upload Students</h5>
      </Card.Header>
      <Card.Body className="p-4">
        {/* Instructions */}
        <Alert variant="info" className="mb-4 border-0" style={{ backgroundColor: '#e7f3ff' }}>
          <div className="d-flex align-items-start">
            <InfoCircle className="text-info me-2 mt-1" size={18} />
            <div>
              <strong className="text-info">Excel Upload Instructions</strong>
              <ul className="mb-0 mt-2 small text-muted">
                <li><strong>File formats:</strong> .xlsx or .xls files only</li>
                <li><strong>File size limit:</strong> Maximum 10MB</li>
                <li><strong>Columns:</strong> Name, Email, Phone</li>
              </ul>
            </div>
          </div>
        </Alert>

        <Form noValidate>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="excel-upload" className="fw-bold">
              Select Excel File *
            </Form.Label>
            <Form.Control
              id="excel-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              disabled={uploading}
              aria-label="Select Excel file"
              size="md"
            />
            {selectedFile && (
              <div className="mt-2 p-2 bg-light rounded">
                <div className="d-flex align-items-center text-success">
                  <CheckCircle className="me-2" size={16} />
                  <strong>Selected file:</strong>
                </div>
                <div className="ms-4 small">
                  <div><strong>Name:</strong> {selectedFile.name}</div>
                  <div><strong>Size:</strong> {formatFileSize(selectedFile.size)}</div>
                </div>
              </div>
            )}
          </Form.Group>

          <Row className="g-3 mb-4">
            <Col xs={12} md={6}>
              <Button
                variant="success"
                className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                size="md"
              >
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FileEarmarkExcel size={18} />
                    Upload & Import Students
                  </>
                )}
              </Button>
            </Col>
            <Col xs={12} md={6}>
              <Button
                variant="outline-primary"
                className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
                onClick={downloadSample}
                disabled={downloadingTemplate}
                size="md"
              >
                {downloadingTemplate ? (
                  <>
                    <span className="spinner-border spinner-border-sm" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Download Template
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Form>

        {/* Upload Progress */}
        {uploading && (
          <div className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold">Processing Excel file...</span>
              <span className="text-muted">Please wait</span>
            </div>
            <ProgressBar animated now={100} variant="primary" style={{ height: '8px' }} />
          </div>
        )}

        {/* Upload Results */}
        {uploadResult && (
          <Alert
            variant={getUploadResultVariant()}
            className="mt-4 border-0 shadow-sm"
            dismissible
            onClose={() => setUploadResult(null)}
          >
            <Alert.Heading className="d-flex align-items-center">
              {uploadResult.failed === 0 && uploadResult.duplicates === 0 ? (
                <CheckCircle className="me-2 text-success" size={24} />
              ) : (
                <XCircle className="me-2 text-warning" size={24} />
              )}
              Import Results Summary
            </Alert.Heading>
            
            {/* Summary Statistics */}
            <Row className="mb-3">
              <Col sm={3}>
                <div className="text-center">
                  <Badge bg="info" className="fs-6 px-3 py-2">
                    {uploadResult.total}
                  </Badge>
                  <div className="small text-muted mt-1">Total Records</div>
                </div>
              </Col>
              <Col sm={3}>
                <div className="text-center">
                  <Badge bg="success" className="fs-6 px-3 py-2">
                    {uploadResult.successful}
                  </Badge>
                  <div className="small text-muted mt-1">Successfully Added</div>
                </div>
              </Col>
              <Col sm={3}>
                <div className="text-center">
                  <Badge bg="warning" className="fs-6 px-3 py-2">
                    {uploadResult.duplicates || 0}
                  </Badge>
                  <div className="small text-muted mt-1">Duplicates Skipped</div>
                </div>
              </Col>
              <Col sm={3}>
                <div className="text-center">
                  <Badge bg="danger" className="fs-6 px-3 py-2">
                    {uploadResult.failed}
                  </Badge>
                  <div className="small text-muted mt-1">Failed</div>
                </div>
              </Col>
            </Row>

            {/* Success Message */}
            {uploadResult.successful > 0 && (
              <div className="alert alert-success border-0 py-2 mb-3">
                <strong>✅ {uploadResult.successful} student{uploadResult.successful !== 1 ? 's' : ''} imported successfully!</strong>
                <div className="small text-muted mt-1">
                  Students can now receive interview invitations.
                </div>
              </div>
            )}

            {/* Error Details */}
            {uploadResult.errors?.length > 0 && (
              <div>
                <strong className="text-danger">❌ Errors encountered:</strong>
                <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.errors.slice(0, 10).map((error, index) => (
                        <tr key={index}>
                          <td><Badge bg="secondary">{error.row}</Badge></td>
                          <td className="small">{error.name}</td>
                          <td className="small">{error.email}</td>
                          <td className="small text-danger">{error.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {uploadResult.errors.length > 10 && (
                    <div className="text-center mt-2">
                      <small className="text-muted">
                        ... and {uploadResult.errors.length - 10} more errors
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Next Steps */}
            {/* {uploadResult.successful > 0 && (
              <div className="mt-3 pt-3 border-top">
                <strong>📋 Next Steps:</strong>
                <ol className="small text-muted mb-0 mt-1">
                  <li>Review the imported students in the student list</li>
                  <li>Send interview invitations to eligible students</li>
                  <li>Students will upload resumes when they book their slots</li>
                </ol>
              </div>
            )} */}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default ExcelUpload;
